import { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../App.css';
import Header from '../components/Header';
import OurHomesMap from '../components/OurHomesMap';
import { Helmet } from 'react-helmet-async';

const HOMES = [
  {
    id: 'harpwood',
    name: 'Harpwood House',
    region: 'Kent',
    address: 'Harpwood House Limited, Seven Mile Lane, Wrotham Heath, Sevenoaks, Kent, TN15 7RY',
    description:
      'Set in a quiet area of Sevenoaks, Harpwood House is one of our newest purpose-built care home developments. The experienced team offer 24-hour nursing care for all residents and care for a variety of complex frailty or dementia-type needs. Currently, phase two of the development is underway to provide further residence for people requiring care.',
    website: 'https://harpwood.house/',
    imageSrc: '/Reception_Cover.jpg',
  },
  {
    id: 'rodwell',
    name: 'Rodwell House',
    region: 'Surrey',
    address: 'Rodwell House, Addlestone, Surrey, KT15 1HH',
    description:
      'Rodwell House, situated in Addlestone, between Weybridge and Woking, provides high quality, affordable accommodation and natural grounds for people who need 24-hour nursing care, particularly those with dementia.',
    website: 'https://www.rodwell.house/',
    imageSrc: '/Rodwell-Front.jpg',
  },
  {
    id: 'harmonia-village',
    name: 'Harmonia Village',
    region: 'Kent',
    address: 'Harmonia Village, 42 Randolph Road, Dover, Kent, CT17 0FZ',
    description:
      "Harmonia Village is a dementia village, equipped to meet its residents' care needs. With 24-hour nursing care on site, Harmonia offers peace of mind for its residents and relatives. Harmonia Village looks and feels like an ordinary village and so is an ideal environment to promote independence for as long as possible.",
    website: 'https://harmoniavillage.care/',
    imageSrc: '/harmonia-005-Edit-scaled.jpg',
  },
  {
    id: 'dover-house',
    name: 'Dover House',
    region: 'Kent',
    address: 'Dover House, 57 Coombe Valley Road, Dover, Kent, CT17 0EX',
    description:
      'Dover House, located in the heart of Dover, offers high quality, affordable accommodation for people who need 24-hour nursing care, particularly those with dementia.',
    website: 'https://www.dover.house/',
    imageSrc: '/doverhouse-043-Edit-scaled.jpg',
  },
  {
    id: 'whitstable-house',
    name: 'Whitstable House',
    region: 'Kent',
    address: 'Whitstable House, Boorman Way, Whitstable, CT5 3SE',
    description:
      'Whitstable House, located overlooking the Thames Estuary, is a brand new, purpose-built home, offering residential and nursing care to people with care needs, particularly those with physical frailty or dementia-type illnesses.',
    website: 'https://www.whitstable.house/',
    imageSrc: '/whitstable-suite-image.jpg',
  },
  {
    id: 'california-house',
    name: 'California House',
    region: 'Suffolk',
    address: 'California House, Ipswich, Suffolk',
    description:
      'California House offers support to people in Ipswich who need rehabilitation or an assessment period before returning home or moving to their long-term place of care.',
    website: 'https://www.californiahouse.care/',
    imageSrc: '/california-image(1).jpg',
  },
];

function OurHomes() {
  const [selectedMapId, setSelectedMapId] = useState<string | null>(null);
  const mapSectionRef = useRef<HTMLDivElement>(null);

  const handleShowOnMap = useCallback((id: string) => {
    setSelectedMapId(id);
    mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <>
      <Helmet>
        <title>Our Homes | Graham Care</title>
        <meta
          name="description"
          content="Explore Graham Care’s purpose-built homes across Surrey, Kent and Suffolk, offering nursing-led care and care suites for complex care needs."
        />
      </Helmet>

      <div className="page our-homes-page-wrap">
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        <Header />

        <main id="main-content">
          <section className="content-section our-homes-page">
            <div className="our-homes-container">
              <h1>Our Homes</h1>

              <div className="our-homes-content">
                <h2>Homes for Life</h2>
                <p>
                  All our homes have care and support supervised by in-house registered nurses, meaning our
                  residents’ care needs can be met for the rest of their lives, without the worry about moving
                  elsewhere.
                </p>

                <h2>Built for Purpose</h2>
                <p>
                  All our buildings are purpose-built, designed with our residents in mind. This means that all
                  our rooms are care suites – larger, homier rooms than those found in traditional nursing homes.
                </p>
              </div>

              <ul className="our-homes-list">
                {HOMES.map((home) => (
                  <li key={home.id} className="our-homes-item">
                    <div className="our-homes-item__image">
                      {home.imageSrc ? (
                        <img src={home.imageSrc} alt={home.name} />
                      ) : (
                        <div
                          className="our-homes-item__placeholder"
                          role="img"
                          aria-label={`${home.name}, ${home.region}`}
                        />
                      )}
                    </div>
                    <div className="our-homes-item__text">
                      <h3>{home.name}</h3>
                      <p className="our-homes-item__region">{home.region}</p>
                      <p className="our-homes-item__address">{home.address}</p>
                      <p className="our-homes-item__bio">{home.description}</p>
                      <p className="our-homes-item__actions">
                        {home.website ? (
                          <a
                            href={home.website}
                            className="our-homes-item__link nurse-care-link"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Visit {home.name}
                          </a>
                        ) : (
                          <Link to="/contact-us" className="our-homes-item__link nurse-care-link">
                            Contact us about {home.name}
                          </Link>
                        )}
                        <span className="our-homes-item__sep"> · </span>
                        <button
                          type="button"
                          className="nurse-care-link our-homes-item__map-btn"
                          onClick={() => handleShowOnMap(home.id)}
                        >
                          Show on Map
                        </button>
                      </p>
                    </div>
                  </li>
                ))}
              </ul>

              <div ref={mapSectionRef}>
                <OurHomesMap selectedId={selectedMapId} />
              </div>
            </div>
          </section>
        </main>

        <footer className="footer">
          <section className="footer__enquiry">
            <div className="footer__enquiry-container">
              <Link to="/contact-us" className="footer__enquiry-link">
                Enquire about Graham Care <span className="footer__arrow">▶</span>
              </Link>
            </div>
          </section>
          <div className="footer__container">
            <section className="contact-section">
              <div className="contact-section__content">
                <p>
                  For enquiries about our homes, please{' '}
                  <Link to="/contact-us" className="nurse-care-link">
                    contact us
                  </Link>
                  .
                </p>
                <p>Copyright © Graham Care Ltd</p>
              </div>
            </section>
          </div>
        </footer>
      </div>
    </>
  );
}

export default OurHomes;
